import { createInfo } from '@liveauctioneers/hammer-ui-core/card/components/createInfo';
import { SubtleMonochromeLink } from '@liveauctioneers/hammer-ui-core/link';
import { useCallback } from 'react';

import { useRecommendationTracking } from '@/hooks/useRecommendationTracking';
import { useAppSelector } from '@/redux/hooks';
import { getItemSummaryCatalogId, getItemSummaryPromoted } from '@/redux/modules/item/summary/itemSummary.selectors';
import { LotRecommendationTrigger } from '@/utils/amplitude/analytics';
import {
    ItemCardSource,
    useTrackItemCardClicked,
} from '@/utils/amplitude/analytics/item/trackItemCardClicked.analytics';
import { getItemUrlFromSlug } from '@/utils/urls';

import { ItemPageItemCardProps } from '../ItemPageItemCard';

export const TitleComponent = createInfo<ItemPageItemCardProps>({
    className: 'line-clamp-0',
    skeletonWidth: 250,
    useText: ({
        itemCardSource = ItemCardSource.LotDetailsPage,
        itemId,
        position,
        recommendationType,
        slugWithLocation,
        title,
    }) => {
        const trackItemCardClicked = useTrackItemCardClicked();
        const catalogId = useAppSelector((state) => getItemSummaryCatalogId(state, itemId));
        const promoted = useAppSelector((state) => getItemSummaryPromoted(state, itemId));
        const onRecommendationClick = useRecommendationTracking({
            isZeroBasedPosition: true,
            itemId,
            recommendationPosition: position ?? 0,
            recommendationType,
        });

        const handleClick = useCallback(
            (trigger: LotRecommendationTrigger) => {
                onRecommendationClick(trigger);
                trackItemCardClicked({
                    catalogId,
                    itemId,
                    promoted,
                    source: itemCardSource,
                });
            },
            [catalogId, itemCardSource, itemId, promoted, trackItemCardClicked, onRecommendationClick]
        );

        return (
            <>
                <SubtleMonochromeLink
                    aria-label={title}
                    className="line-clamp-2 group-[.image-hover]:text-link-hover group-[.image-hover]:underline"
                    href={getItemUrlFromSlug(itemId, slugWithLocation)}
                    onClick={() => {
                        handleClick(LotRecommendationTrigger.TitleClick);
                    }}
                    title={title}
                >
                    {title}
                </SubtleMonochromeLink>
            </>
        );
    },
});
